import axios from "axios";

function extend(data) {
  for (const key in data) {
    this[key] = data[key];
  }
}

function start() {
  this._loading = true;
}

async function complete(request) {
  try {
    const response = await request;
    return update.call(this, response ? response.data : {});
  } finally {
    this._loading = false;
  }
}

function update(data) {
  if (data.results) {
    return new Paginator({
      ...data,
      _url: this._url,
      _params: this._params,
      _class: this._class,
      results: data.results.map(result => {
        return new this._class(result);
      })
    });
  } else {
    return new this._class(data);
  }
}

async function get(url, params) {
  start.call(this);
  this._params = params || {};
  const request = axios.get(url, { params });
  return await complete.call(this, request);
}

function Paginator(data) {
  extend.call(this, data);
  this._loading = false;
  this._page = 1;
  this._currentPage = 1;
  this._limit = this._params.limit;
  this._offset = this._params.offset || 0;

  if (!this._limit) {
    const url = this.next || this.prev;
    if (!url) {
      return;
    }

    const limit = url
      .split("?")[1]
      .split("&")
      .find(parameter => parameter.split("=")[0] === "limit");

    if (!limit) {
      return;
    }

    this._limit = parseInt(limit.split("=")[1]);
  }

  this._page = Math.ceil(this.count / this._limit);
  this._currentPage = parseInt(this._offset / this._limit) + 1;
  this._isNoMore = this._page === this._currentPage;
}

Paginator.prototype.goTo = function(page) {
  return get.call(this, this._url, {
    ...this._params,
    limit: this._limit,
    offset: (page - 1) * this._limit
  });
};

Paginator.prototype.loadMore = async function() {
  if (this._currentPage >= this._page) {
    return this;
  }

  const s = await this.goTo(this._currentPage + 1);
  this.results.push(...s.results);
  s.results = this.results;
  return s;
};

function Resource(data) {
  extend.call(this, data);
  this._key = "id";
  this._loading = false;
}

Resource.prototype.extend = function(data) {
  extend.call(this, data);
};

Resource.prototype.get = function(params) {
  return get.call(this, this._url, params);
};

Resource.prototype.load = function() {
  if (!(this._key in this)) {
    throw new ReferenceError();
  }

  return get.call(this, `${this._url}${this[this._key]}/`);
};

Resource.prototype.save = async function() {
  start.call(this);

  let request = null;
  if (this._key in this && this[this._key]) {
    request = axios.put(`${this._url}${this[this._key]}/`, this);
  } else {
    request = axios.post(this._url, this);
  }

  return await complete.call(this, request);
};

Resource.prototype.delete = async function() {
  if (!(this._key in this)) {
    throw new ReferenceError();
  }

  start.call(this);

  return await complete.call(
    this,
    axios.delete(`${this._url}${this[this._key]}/`)
  );
};

Resource.prototype.preSave = async function(preservedFieldNames) {
  const preservedFields = preservedFieldNames.map(field => {
    const value = this[field];
    this[field] = null;
    return value;
  });

  const saved = await Resource.prototype.save.call(this);
  this[this._key] = saved[this._key];

  preservedFieldNames.forEach((field, i) => {
    this[field] = preservedFields[i];
  });
};

Resource.prototype.presign = async function(blob, data) {
  if (!this[this._key]) {
    throw new ReferenceError();
  }

  const { height, width } = await new Promise(resolve => {
    const image = new Image();
    image.onload = function() {
      resolve({
        height: this.height,
        width: this.width
      });
    };
    image.onerror = () => {
      resolve({});
    };
    image.src = URL.createObjectURL(blob);
  });

  data.height = height;
  data.width = width;

  try {
    const response = await axios.post(
      `${this._url}${this[this._key]}/presign/`,
      data
    );
    return response.data;
  } finally {
    document.body.classList.remove("loading-open");
  }
};

export function Artwork(data) {
  Resource.call(this, data);
  this._class = Artwork;
  this._url = "/api/v1/artworks/";
}

Artwork.prototype = Object.create(Resource.prototype);
Artwork.prototype.constructor = Artwork;

Artwork.prototype.mine = async function(params) {
  return get.call(this, `/api/v1/users/${window.user.id}/artworks/`, params);
};

async function getBlobInfo(objectURL) {
  if (!objectURL) {
    return null;
  }

  const split = objectURL.split("/");
  const filename = split[split.length - 1];
  const result = await axios.get(objectURL, {
    responseType: "blob"
  });

  if (result.status !== 200) {
    return null;
  }

  return {
    filename,
    blob: result.data,
    type: result.data.type
  };
}

Artwork.prototype.save = async function() {
  const blobs = ["cover", "cropped_cover"];
  const includesBlobs = blobs.some(field => {
    return this[field] && this[field].startsWith("blob:");
  });

  if (includesBlobs) {
    if (!this[this._key]) {
      await this.preSave(blobs);
    }

    for (let field of blobs) {
      // Only new images; an existing /files/ URL would be downloaded and re-uploaded.
      if (!this[field] || !this[field].startsWith("blob:")) {
        continue;
      }

      const blobInfo = await getBlobInfo(this[field]);
      if (!blobInfo) {
        continue;
      }

      const presigned = await this.presign(blobInfo.blob, {
        filename: blobInfo.filename,
        type: blobInfo.type
      });

      await axios.put(presigned.url, blobInfo.blob, {
        headers: {
          "Content-Type": blobInfo.type,
          "x-amz-acl": "public-read"
        }
      });

      this[field] = presigned.filename;
    }
  }

  return Resource.prototype.save.call(this);
};

Artwork.prototype.byUser = async function(userId, params) {
  this._url = `/api/v1/users/${userId}/artworks/`;
  return await get.call(this, `/api/v1/users/${userId}/artworks/`, params);
};

Artwork.prototype.byInvite = async function(inviteId, params) {
  this._url = `/api/v1/invites/${inviteId}/artworks/`;
  return await get.call(this, `/api/v1/invites/${inviteId}/artworks/`, params);
};

export function User(data) {
  Resource.call(this, data);
  this._class = User;
  this._url = "/api/v1/users/";
}

User.prototype = Object.create(Resource.prototype);
User.prototype.constructor = User;

User.prototype.save = async function() {
  const blobs = ["avatar", "cropped_avatar", "banner", "cropped_banner"];
  const includesBlobs = blobs.some(field => {
    return this[field] && this[field].startsWith("blob:");
  });

  if (includesBlobs) {
    if (!this[this._key]) {
      await this.preSave(blobs);
    }

    for (let field of blobs) {
      if (!this[field] || !this[field].startsWith("blob:")) {
        continue;
      }

      const blobInfo = await getBlobInfo(this[field]);
      if (!blobInfo) {
        continue;
      }

      const presigned = await this.presign(blobInfo.blob, {
        filename: blobInfo.filename,
        type: blobInfo.type
      });

      await axios.put(presigned.url, blobInfo.blob, {
        headers: {
          "Content-Type": blobInfo.type,
          "x-amz-acl": "public-read"
        }
      });

      this[field] = presigned.filename;
    }
  }

  return Resource.prototype.save.call(this);
};

User.prototype.changePassword = async function(data) {
  if (!this[this._key]) {
    throw new ReferenceError();
  }

  return (
    await axios.post(`${this._url}${this[this._key]}/password_change/`, data)
  ).data;
};

User.prototype.resetPassword = async function(data) {
  return (await axios.post(`${this._url}password_reset/`, data)).data;
};

User.prototype.reset = async function(data) {
  return (await axios.post(`${this._url}reset/`, data)).data;
};

export function Collection(data) {
  Resource.call(this, data);
  this._class = Collection;
  this._url = "/api/v1/collections/";
}

Collection.prototype = Object.create(Resource.prototype);
Collection.prototype.constructor = Collection;

export function Invite(data) {
  Resource.call(this, data);
  this._class = Invite;
  this._url = "/api/v1/invites/";
}

Invite.prototype = Object.create(Resource.prototype);
Invite.prototype.constructor = Invite;

export function InvitePage(data) {
  Resource.call(this, data);
  this._class = InvitePage;
  this._url = "/api/v1/invite-pages/";
}

InvitePage.prototype = Object.create(Resource.prototype);
InvitePage.prototype.constructor = InvitePage;

export function InviteColumn(data) {
  Resource.call(this, data);
  this._class = InviteColumn;
  this._url = "/api/v1/invite-columns/";
}

InviteColumn.prototype = Object.create(Resource.prototype);
InviteColumn.prototype.constructor = InviteColumn;

export function InviteApplication(data) {
  Resource.call(this, data);
  this._class = InviteApplication;
  this._url = "/api/v1/invite-applications/";
}

InviteApplication.prototype = Object.create(Resource.prototype);
InviteApplication.prototype.constructor = InviteApplication;

export function InviteQuestion(data) {
  Resource.call(this, data);
  this._class = InviteQuestion;
  this._url = "/api/v1/invite-questions/";
}

InviteQuestion.prototype = Object.create(Resource.prototype);
InviteQuestion.prototype.constructor = InviteQuestion;

export function InviteAnswer(data) {
  Resource.call(this, data);
  this._class = InviteAnswer;
  this._url = "/api/v1/invite-answers/";
}

InviteAnswer.prototype = Object.create(Resource.prototype);
InviteAnswer.prototype.constructor = InviteAnswer;

export function InviteSubscription(data) {
  Resource.call(this, data);
  this._class = InviteSubscription;
  this._url = "/api/v1/invite-subscriptions/";
}

InviteSubscription.prototype = Object.create(Resource.prototype);
InviteSubscription.prototype.constructor = InviteSubscription;
